import { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';

/**
 * Single custom cursor: a small dot that grows into a ring over
 * interactive elements. Desktop (fine pointer) only.
 * Uses refs for hover state to avoid React re-renders on every mousemove.
 */
export function Cursor() {
  const rawX = useMotionValue(-100);
  const rawY = useMotionValue(-100);
  const x = useSpring(rawX, { stiffness: 700, damping: 48, mass: 0.2 });
  const y = useSpring(rawY, { stiffness: 700, damping: 48, mass: 0.2 });
  const size = useMotionValue(10);
  const opacity = useMotionValue(0);
  const activeRef = useRef(false);
  const raf = useRef(0);
  const pending = useRef({ x: -100, y: -100, active: false, visible: false });

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      document.documentElement.classList.add('has-cursor');
    });

    const flush = () => {
      raf.current = 0;
      const p = pending.current;
      rawX.set(p.x);
      rawY.set(p.y);
      opacity.set(p.visible ? 1 : 0);
      if (p.active !== activeRef.current) {
        activeRef.current = p.active;
        size.set(p.active ? 44 : 10);
      }
    };

    const onMove = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      pending.current = {
        x: e.clientX,
        y: e.clientY,
        visible: true,
        active: !!(t && t.closest('a, button, [data-cursor]')),
      };
      if (!raf.current) raf.current = requestAnimationFrame(flush);
    };
    const onLeave = () => {
      pending.current.visible = false;
      if (!raf.current) raf.current = requestAnimationFrame(flush);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(id);
      document.documentElement.classList.remove('has-cursor');
      window.removeEventListener('mousemove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [rawX, rawY, size, opacity]);

  return (
    <motion.div
      aria-hidden
      style={{
        x,
        y,
        width: size,
        height: size,
        opacity,
        translateX: '-50%',
        translateY: '-50%',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 200,
        pointerEvents: 'none',
        borderRadius: '50%',
        mixBlendMode: 'difference',
        background: 'var(--cursor)',
      }}
    />
  );
}
